'use client';

import {
  Alert,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Link,
  Span,
  Stack,
  Text,
} from '@chakra-ui/react';
import { FirebaseError } from 'firebase/app';
import { getAuth, GoogleAuthProvider, sendSignInLinkToEmail, signInWithPopup } from 'firebase/auth';
import { isEmpty } from 'lodash';
import debounce from 'lodash/debounce';
import { memo, useCallback, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { CiMail } from 'react-icons/ci';
import { FaGoogle } from 'react-icons/fa';

import { Field } from '@components/ui/field';
import { InputGroup } from '@components/ui/input-group';
import LoginRedirect from '@features/login/LoginRedirect';
import ResendEmail from '@features/login/ResendEmail';
import {
  clearVerificationEmail,
  EMAIL_VALIDATION,
  getVerificationEmail,
  setVerificationEmail,
} from 'helpers/email';
import { didCancelPopup, handleFirebaseError } from 'helpers/handleFirebaseError';
import { login } from 'server/actions/login';
import { ServerMeQuery } from 'server/actions/userQuery';

interface Props {
  loggedInUser?: ServerMeQuery['me'];
  signUp?: boolean;
  onClickSignUp?: (signUp: boolean) => void;
  loginTitle?: string;
  loginDescription?: string;
  redirectUrl?: string;
}

interface FormState {
  email: string;
  errors: {
    email?: string;
  };
}

const EMAIL_DEBOUNCE_TIME_MS = 500;

const LoginForm: React.FC<Props> = ({
  loggedInUser,
  signUp = false,
  onClickSignUp,
  loginTitle,
  loginDescription,
  redirectUrl,
}) => {
  const [action, setAction] = useState<
    'googleSignIn' | 'anonymousSignIn' | 'phoneSignIn' | 'logout' | 'sendEmailVerification' | null
  >(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const pendingEmailToVerify = getVerificationEmail() ?? '';
  const [formState, setFormState] = useState<FormState>({
    email: pendingEmailToVerify,
    errors: {},
  });
  const [sentEmailVerification, setSentEmailVerification] = useState(
    pendingEmailToVerify.length > 0,
  );
  const [resentCount, setResentCount] = useState(0);

  const handleError = useCallback((reason: FirebaseError) => {
    if (didCancelPopup(reason)) {
      setActionLoading(false);
    }
    const message = handleFirebaseError(reason);
    if (message != null) {
      setLoginError(message);
    }
  }, []);

  const loginUser = useCallback(
    (token: string) => {
      const callback =
        redirectUrl != null && redirectUrl.length > 0
          ? redirectUrl
          : window.location.href.replace(window.location.search, '');
      login(token, callback);
    },
    [redirectUrl],
  );

  const googleLoginCallback = useCallback(() => {
    setAction('googleSignIn');
    setActionLoading(true);
    const provider = new GoogleAuthProvider();

    signInWithPopup(getAuth(), provider).then((result) => {
      const { user } = result;
      if (user !== null) {
        user.getIdToken().then((token: string) => {
          if (token != null) {
            loginUser(token);
          }
        });
      }
    }, handleError);
  }, [handleError, loginUser]);

  const onChangeEmail = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const validation = EMAIL_VALIDATION.validate(value);
    let errMsg: string | undefined;
    if (value.length === 0) {
      errMsg = 'Email is required';
    } else if (validation.error) {
      errMsg = 'Invalid email';
    }
    setFormState((prev) => {
      return { ...prev, email: value, errors: { ...prev.errors, email: errMsg } };
    });
  }, []);

  const sendEmailVerification = useCallback(() => {
    if (formState.email.length === 0 || formState.errors.email != null) {
      return;
    }
    setAction('sendEmailVerification');
    setActionLoading(true);

    sendSignInLinkToEmail(getAuth(), formState.email, {
      url: `${window.location.origin}/emailVerification`,
      handleCodeInApp: true,
    }).then(() => {
      setVerificationEmail(formState.email);
      setSentEmailVerification(true);
      setActionLoading(false);
    }, handleError);
  }, [handleError, formState.email, formState.errors.email]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        sendEmailVerification();
      }
    },
    [sendEmailVerification],
  );

  const resendEmailVerification = useCallback(() => {
    setResentCount((prev) => prev + 1);
    sendEmailVerification();
  }, [sendEmailVerification]);

  const loginWithOtherMethods = useCallback(() => {
    setResentCount(0);
    setSentEmailVerification(false);
    setFormState({
      email: '',
      errors: {},
    });
    clearVerificationEmail();
  }, []);

  const onClickSignInActionLinks = useCallback(() => {
    if (onClickSignUp != null) {
      onClickSignUp(signUp);
      return;
    }
    if (signUp) {
      window.location.href = '/login';
    } else {
      window.location.href = '/signup';
    }
  }, [signUp, onClickSignUp]);

  const title = useMemo(() => {
    if (loginTitle != null) {
      return loginTitle;
    }
    return signUp ? 'Create a nullifyrisk account' : 'Sign in';
  }, [loginTitle, signUp]);

  if (sentEmailVerification) {
    return (
      <ResendEmail
        resentCount={resentCount}
        resendEmailVerification={resendEmailVerification}
        loginWithOtherMethods={loginWithOtherMethods}
      />
    );
  }

  return (
    <Flex justify="center">
      <Box w="md" p={8}>
        {loggedInUser != null && <LoginRedirect />}
        {loggedInUser == null && (
          <Stack gap={4}>
            {loginError != null && (
              <Alert.Root status="error">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>Login failed</Alert.Title>
                  <Alert.Description>{loginError}</Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}
            <Stack display="flex" justifyContent="center" maxW="sm">
              {!isEmpty(title) && <Heading size="3xl">{title}</Heading>}
              {!isEmpty(loginDescription) && (
                <Text fontSize="md" color="fg.muted">
                  {loginDescription}
                </Text>
              )}
              <Text fontSize="md" color="fg.muted">
                {signUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                <Link onClick={onClickSignInActionLinks}>
                  <Span>{signUp ? 'Log in' : 'Sign up'}</Span>
                </Link>
              </Text>
            </Stack>
            {!isMobile && (
              <Stack gap={4}>
                <Button
                  loading={actionLoading && action === 'googleSignIn'}
                  variant="secondaryOutline"
                  onClick={googleLoginCallback}
                >
                  <FaGoogle />
                  Continue with Google
                </Button>
                <Text fontSize="sm">Or continue with email</Text>
              </Stack>
            )}
            <Stack gap={4}>
              <Field invalid={formState.errors.email != null} errorText={formState.errors.email}>
                <InputGroup width="full" startElement={<CiMail />}>
                  <Input
                    placeholder="me@example.com"
                    paddingInline={3}
                    onChange={debounce(onChangeEmail, EMAIL_DEBOUNCE_TIME_MS)}
                    onKeyDown={onKeyDown}
                    borderColor={formState.errors.email != null ? 'undefined' : 'border.light'}
                  />
                </InputGroup>
              </Field>
              <Button
                loading={actionLoading && action === 'sendEmailVerification'}
                fontWeight="medium"
                variant="solid"
                onClick={sendEmailVerification}
                disabled={formState.email.length === 0 || formState.errors.email != null}
              >
                Continue
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </Flex>
  );
};

export default memo(LoginForm);
